import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Toast } from '#components/toast/toast.js';
import { useAuth0Context } from '#state/contexts/auth0-context.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { isAuthFlowPathname } from '#utils/auth-flow-routes.js';
import { buildLoginRedirectOptions, collectLoginCampaignTags } from '#utils/auth0-campaign.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';

import type { Auth0AppState } from '#utils/auth0-app-state.js';

export const AUTH_ERROR_RELOGIN_KEY = 'cadstrom:auth-error-relogin-attempted';

interface UnauthenticatedErrorDisplayProps {
	/**
	 * When true, the automatic redirect to login fires at most once per session,
	 * then falls back to the manual "Log in" card. Use for errors that might
	 * persist after re-login (e.g. a backend `UNAUTHORIZED`), so the redirect
	 * can't loop. When false (default), the redirect behaves as before.
	 */
	guarded?: boolean;
}

export function UnauthenticatedErrorDisplay({ guarded = false }: UnauthenticatedErrorDisplayProps) {
	const el = useDataElTagGenerator(UnauthenticatedErrorDisplay);
	const auth0 = useAuth0Context();
	const addToast = useAddToast();

	const [isLoading, setIsLoading] = useState(false);
	const [redirectFailed, setRedirectFailed] = useState(false);

	const handleLogIn = useCallback(() => {
		setIsLoading(true);

		void auth0.client
			.loginWithRedirect<Auth0AppState>(
				buildLoginRedirectOptions({
					returnTo: window.location.pathname + window.location.search + window.location.hash,
					tags: collectLoginCampaignTags(),
				}),
			)
			.catch((error: unknown) => {
				setIsLoading(false);
				setRedirectFailed(true);
				addToast(
					<Toast severity='error' title='Login failed'>
						{error instanceof Error ? error.message : 'Failed to log in.'}
					</Toast>,
				);
			});
	}, [addToast, auth0.client]);

	// A guarded error may persist after logging in, so only auto-redirect if we
	// haven't already attempted a re-login this session — otherwise it could loop.
	const alreadyAttempted = guarded && sessionStorage.getItem(AUTH_ERROR_RELOGIN_KEY) !== null;
	const shouldRedirect = !isAuthFlowPathname(window.location.pathname) && !alreadyAttempted;

	const didRedirect = useRef(false);
	useEffect(() => {
		if (!shouldRedirect || didRedirect.current) {
			return;
		}
		didRedirect.current = true;
		if (guarded) {
			sessionStorage.setItem(AUTH_ERROR_RELOGIN_KEY, '1');
		}
		handleLogIn();
	}, [shouldRedirect, handleLogIn, guarded]);

	if (shouldRedirect && !redirectFailed) {
		return (
			<div {...el('redirecting')} className='grid min-h-0 min-w-0 grow place-items-center p-4'>
				<div className='flex flex-col items-center gap-3'>
					<CircularProgress />
					<p className='text-gray-a11'>Redirecting...</p>
				</div>
			</div>
		);
	}

	return (
		<div {...el()} className='grid min-h-0 min-w-0 grow place-items-center p-4'>
			<div {...el('container')} className='flex flex-col gap-6'>
				<div className='flex min-w-0 grow basis-auto flex-col gap-3'>
					<h1 className='text-6xl font-bold text-gray-a9'>401</h1>
					<h2 className='text-3xl font-bold'>Unauthenticated</h2>
					<p className='text-gray-a11'>You need to be logged in to access this page.</p>
				</div>

				<Button loading={isLoading} onClick={handleLogIn}>
					<span>Log in</span>
				</Button>
			</div>
		</div>
	);
}
